import detectEthereumProvider from '@metamask/detect-provider';
import React from 'react';

// Constants
const AVAX_FUJI_CHAIN_ID = process.env.REACT_APP_CHAIN_ID || '0xa869';
const AVAX_FUJI_PARAMS = {
  chainId: process.env.REACT_APP_CHAIN_ID || '0xa869',
  chainName: process.env.REACT_APP_CHAIN_NAME || 'Avalanche Fuji Testnet',
  nativeCurrency: {
    name: process.env.REACT_APP_NATIVE_CURRENCY_NAME || 'Avalanche',
    symbol: process.env.REACT_APP_NATIVE_CURRENCY_SYMBOL || 'AVAX',
    decimals: parseInt(process.env.REACT_APP_NATIVE_CURRENCY_DECIMALS) || 18
  },
  rpcUrls: [process.env.REACT_APP_PROVIDER_URL || 'https://api.avax-test.network/ext/bc/C/rpc'],
  blockExplorerUrls: [process.env.REACT_APP_BLOCK_EXPLORER_URL || 'https://testnet.snowtrace.io/']
};

// Enhanced provider detection that handles multiple wallets
export const getMetaMaskProvider = async () => {
  try {
    // First, try to detect if window.ethereum exists
    if (typeof window.ethereum === 'undefined') {
      return null;
    }

    // Check if there are multiple providers
    if (window.ethereum.providers && window.ethereum.providers.length > 0) {
      console.log('Multiple providers detected:', window.ethereum.providers.length);
      
      // Find MetaMask specifically
      const metaMaskProvider = window.ethereum.providers.find(
        provider => provider.isMetaMask && !provider.isBraveWallet
      );
      
      if (metaMaskProvider) {
        console.log('MetaMask provider found in multi-provider setup');
        return metaMaskProvider;
      }
    }
    
    // Check if the single provider is MetaMask
    if (window.ethereum.isMetaMask && !window.ethereum.isBraveWallet) {
      console.log('Single MetaMask provider detected');
      return window.ethereum;
    }
    
    // Fallback to detectEthereumProvider for better compatibility
    const provider = await detectEthereumProvider({
      silent: true,
      timeout: 1000
    });
    
    if (provider && provider.isMetaMask && !provider.isBraveWallet) {
      return provider;
    }
    
    return null;
  } catch (error) {
    console.error('Error detecting MetaMask provider:', error);
    return null;
  }
};

// Enhanced provider detection with EIP-6963 support
export const getProviderWithEIP6963 = async () => {
  return new Promise((resolve) => {
    const providers = [];
    let timeoutId;
    
    // Listen for provider announcements
    const handleAnnouncement = (event) => {
      providers.push(event.detail);
    };
    
    window.addEventListener('eip6963:announceProvider', handleAnnouncement);
    
    // Request providers to announce themselves
    window.dispatchEvent(new Event('eip6963:requestProvider'));
    
    // Set a timeout to resolve after collecting providers
    timeoutId = setTimeout(() => {
      window.removeEventListener('eip6963:announceProvider', handleAnnouncement);
      
      // Find MetaMask provider
      const metaMaskProvider = providers.find(p => 
        p.info.name.toLowerCase().includes('metamask') && 
        !p.info.name.toLowerCase().includes('brave')
      );
      
      if (metaMaskProvider) {
        console.log('MetaMask found via EIP-6963:', metaMaskProvider.info.name);
        resolve(metaMaskProvider.provider);
      } else {
        // Fallback to traditional detection
        getMetaMaskProvider().then(resolve);
      }
    }, 100);
  });
};

// Improved mobile detection with more comprehensive checks
export const isMobile = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  
  // Check for touch devices as an additional indicator
  const hasTouchScreen = () => {
    return ('ontouchstart' in window) || 
           (navigator.maxTouchPoints > 0) || 
           (navigator.msMaxTouchPoints > 0);
  };
  
  // Standard mobile user agent check
  const mobileCheck = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
  
  // Return true if either check passes
  return mobileCheck || hasTouchScreen();
};

// Enhanced Android detection
export const isAndroid = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return /Android/i.test(userAgent);
};

// Improved in-app browser detection
export const isInAppBrowser = () => {
  const userAgent = navigator.userAgent || navigator.vendor || window.opera;
  return (
    /Instagram/.test(userAgent) ||
    /FBAV/.test(userAgent) || // Facebook
    /Line/.test(userAgent) ||
    /wv/.test(userAgent) || // Android WebView
    /FBAN/.test(userAgent) || // Facebook app
    /Twitter/.test(userAgent) ||
    /TikTok/.test(userAgent)
  );
};

// Handle mobile deep linking to MetaMask with fallback options
export const openMobileWallet = () => {
  try {
    // Remember the current URL to return to after connection
    const currentUrl = window.location.href;
    sessionStorage.setItem('returnToUrl', currentUrl);
    
    // Get current URL without protocol for deep linking
    const strippedUrl = currentUrl.replace(/^https?:\/\//, '');
    
    // Create MetaMask deep link
    const mmDeepLink = 'https://metamask.app.link/dapp/';
    console.log("Redirecting to MetaMask with deep link:", `${mmDeepLink}${strippedUrl}`);
    
    // Create an iframe for iOS to avoid popup blockers
    if (/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = `${mmDeepLink}${strippedUrl}`;
      document.body.appendChild(iframe);
      
      // Set a small timeout and then remove the iframe
      setTimeout(() => {
        document.body.removeChild(iframe);
        
        // Fallback to direct redirection if needed
        setTimeout(() => {
          window.location.href = `${mmDeepLink}${strippedUrl}`;
        }, 500);
      }, 100);
      
      return true;
    } else {
      // For Android, use direct redirection
      window.location.href = `${mmDeepLink}${strippedUrl}`;
      return true;
    }
  } catch (error) {
    console.error("Error during deep linking:", error);
    return false;
  }
};

// Try alternate deep linking methods for Android
export const tryAlternateDeepLink = () => {
  try {
    // Alternative deep link format
    const currentHostname = window.location.hostname;
    const currentPath = window.location.pathname + window.location.search;
    
    // Try intent URL first (newer Android approach)
    const intentUrl = `intent://${currentHostname}${currentPath}#Intent;scheme=https;package=io.metamask;end`;
    console.log("Trying alternate intent URL:", intentUrl);
    window.location.href = intentUrl;
    
    // Set a fallback to direct app download
    setTimeout(() => {
      window.location.href = 'https://play.google.com/store/apps/details?id=io.metamask';
    }, 2500);
    
    return true;
  } catch (error) {
    console.error("Error during alternative deep linking:", error);
    return false;
  }
};

// Check if the browser supports Web3
export const isWeb3Supported = () => {
  return typeof window !== 'undefined' && 
         (typeof window.ethereum !== 'undefined' || 
          typeof window.web3 !== 'undefined');
};

// Safely check if provider exists before attempting to switch networks
const switchNetwork = async (provider) => {
  if (!provider) {
    console.log("Provider not available, skipping network switch");
    return;
  }
  
  try {
    const chainId = await provider.request({ method: 'eth_chainId' });
    
    if (chainId !== AVAX_FUJI_CHAIN_ID) {
      try {
        // Try to switch to the network
        await provider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId: AVAX_FUJI_CHAIN_ID }],
        });
      } catch (switchError) {
        // The network hasn't been added to MetaMask yet
        if (switchError.code === 4902) {
          try {
            await provider.request({
              method: 'wallet_addEthereumChain',
              params: [AVAX_FUJI_PARAMS],
            });
          } catch (addError) {
            console.error("Failed to add network to wallet", addError);
            throw addError;
          }
        } else {
          console.error("Failed to switch network", switchError);
          throw switchError;
        }
      }
    }
  } catch (error) {
    console.error("Error in switchNetwork:", error);
    throw error;
  }
};

// Enhanced Android-specific connection with better provider detection
export const connectAndroid = async () => {
  console.log("Attempting Android connection...");
  
  // Try EIP-6963 first, then fallback to traditional detection
  let provider = await getProviderWithEIP6963();
  
  if (!provider) {
    provider = await getMetaMaskProvider();
  }
  
  if (provider) {
    console.log("Provider detected in Android");
    try {
      // Request accounts first to ensure connection
      const accounts = await provider.request({ method: 'eth_requestAccounts' });
      
      // Then switch network after connection is established
      await switchNetwork(provider);
      
      return { address: accounts[0] };
    } catch (error) {
      console.error("Error connecting in Android with provider:", error);
      
      // Special handling for common Android errors
      if (error.code === -32002) {
        return {
          address: "",
          status: "MetaMask is already processing a request. Please check your MetaMask app."
        };
      }
      
      return { 
        address: "", 
        status: `Error connecting: ${error.message}` 
      };
    }
  } else {
    console.log("No provider detected, trying deep link...");
    
    // Check if we're in an in-app browser which won't support deep linking
    if (isInAppBrowser()) {
      return {
        address: "",
        status: "You're using an in-app browser which doesn't support MetaMask. Please open this page in your device's native browser."
      };
    }
    
    // We need to redirect to the MetaMask app
    const redirected = openMobileWallet();
    
    if (!redirected) {
      console.log("Primary deep link failed, trying alternate method...");
      tryAlternateDeepLink();
    }
    
    return { 
      address: "", 
      status: "Opening MetaMask app... If nothing happens, please install MetaMask from Google Play Store." 
    };
  }
};

// Enhanced iOS-specific connection handling
export const connectIOS = async () => {
  console.log("Attempting iOS connection...");
  
  // Try EIP-6963 first, then fallback to traditional detection
  let provider = await getProviderWithEIP6963();
  
  if (!provider) {
    provider = await getMetaMaskProvider();
  }
  
  if (provider) {
    try {
      // Request accounts first to ensure connection
      const accounts = await provider.request({ method: 'eth_requestAccounts' });
      
      // Then switch network after connection is established
      await switchNetwork(provider);
      
      return { address: accounts[0] };
    } catch (error) {
      console.error("Error connecting on iOS:", error);
      
      // Handle common iOS-specific errors
      if (error.code === -32002) {
        return {
          address: "",
          status: "Request pending in MetaMask. Please open the MetaMask app."
        };
      }
      
      return { address: "", status: "Error connecting: " + error.message };
    }
  } else {
    // Attempt to open MetaMask mobile app
    openMobileWallet();
    
    return { 
      address: "", 
      status: "Opening MetaMask app... If nothing happens, please install MetaMask from the App Store." 
    };
  }
};

export const connectWallet = async () => {
  console.log("Connect wallet called, mobile:", isMobile(), "android:", isAndroid());
  
  // Special handling for Android
  if (isMobile() && isAndroid()) {
    return connectAndroid();
  }
  
  // Special handling for iOS
  if (isMobile() && /iPhone|iPad|iPod/i.test(navigator.userAgent)) {
    return connectIOS();
  }
  
  // Handle other mobile platforms
  if (isMobile()) {
    // Try EIP-6963 first, then fallback to traditional detection
    let provider = await getProviderWithEIP6963();
    
    if (!provider) {
      provider = await getMetaMaskProvider();
    }
    
    if (provider) {
      try {
        // Always request accounts first to establish connection
        const accounts = await provider.request({ method: 'eth_requestAccounts' });
        
        // After connection is established, try to switch networks
        await switchNetwork(provider);
        
        return { address: accounts[0] };
      } catch (error) {
        console.error("Error connecting on mobile:", error);
        return { address: "", status: "Error connecting: " + error.message };
      }
    } else {
      openMobileWallet();
      return { 
        address: "", 
        status: "Opening MetaMask mobile app... If nothing happens, please install MetaMask from your app store." 
      };
    }
  } else {
    // Desktop flow with enhanced provider detection
    try {
      // Try EIP-6963 first for better multi-wallet support
      let provider = await getProviderWithEIP6963();
      
      if (!provider) {
        provider = await getMetaMaskProvider();
      }
      
      if (!provider) {
        return {
          address: "",
          status: (
            <span>
              <p>
                🦊 <a target="_blank" rel="noopener noreferrer" href={`https://metamask.io/download.html`}>
                  You must install MetaMask, a virtual Ethereum wallet, in your browser.
                </a>
              </p>
            </span>
          ),
        };
      }
      
      // Always request accounts first to establish connection
      const accounts = await provider.request({ method: 'eth_requestAccounts' });
      
      // After connection is established, try to switch networks
      await switchNetwork(provider);
      
      return { address: accounts[0] };
    } catch (error) {
      console.error("Error in desktop connection:", error);
      
      // Special handling for common desktop errors
      if (error.code === -32002) {
        return {
          address: "",
          status: "MetaMask is already processing a request. Please check your MetaMask extension."
        };
      }
      
      return { address: "", status: "Error: " + error.message };
    }
  }
};

export const getCurrentWalletConnected = async () => {
  try {
    // Try EIP-6963 first for better multi-wallet support
    let provider = await getProviderWithEIP6963();
    
    if (!provider) {
      provider = await getMetaMaskProvider();
    }
    
    if (!provider) {
      if (isMobile()) {
        return {
          address: "",
          status: "No wallet detected. Please connect with MetaMask mobile app.",
        };
      } else {
        return {
          address: "",
          status: (
            <span>
              <p>
                🦊 <a target="_blank" rel="noopener noreferrer" href={`https://metamask.io/download.html`}>
                  You must install MetaMask to use this dApp.
                </a>
              </p>
            </span>
          ),
        };
      }
    }
    
    // Check if already connected by getting accounts
    const accounts = await provider.request({ method: 'eth_accounts' });
    
    if (accounts.length > 0) {
      // Only attempt to switch networks if we're already connected
      try {
        await switchNetwork(provider);
      } catch (networkError) {
        console.warn("Network switch failed but continuing with connected account", networkError);
      }
      
      return {
        address: accounts[0],
      };
    } else {
      return {
        address: "",
        status: "🦊 Connect to wallet using the connect button.",
      };
    }
  } catch (error) {
    console.error("Error in getCurrentWalletConnected:", error);
    return {
      address: "",
      status: "Error checking wallet connection: " + error.message,
    };
  }
};